import React from 'react';
import { cn } from '@/lib/utils';
import type { GraphRunDetail } from './useGraphRuns';
import type { RunCompatibility } from './helpers/runCompatibility';

function formatRelativeTime(dateString: string): string {
    const now = Date.now();
    const then = new Date(dateString).getTime();
    const seconds = Math.floor((now - then) / 1000);

    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(dateString).toLocaleDateString();
}

function formatExactTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 3,
    });
}

function formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    const seconds = (ms / 1000).toFixed(1);
    return `${seconds}s`;
}

interface Props {
    runDetail: GraphRunDetail | null;
    isLoading: boolean;
    onClose: () => void;
    compatibility: RunCompatibility | null;
    livePreviewEnabled: boolean;
    onToggleLivePreview?: () => void;
}

export const RunInfoBar: React.FC<Props> = ({ runDetail, isLoading, onClose, compatibility, livePreviewEnabled, onToggleLivePreview }) => {
    return (
        <div className="absolute bottom-6 left-0 right-0 flex justify-center pointer-events-none">
            <div
                className={cn(
                    'pointer-events-auto',
                    'flex items-center gap-3 px-5 py-3',
                    'bg-white/70 dark:bg-neutral-900/70 backdrop-blur-xl',
                    'rounded-2xl border border-white/30 dark:border-neutral-700/50 shadow-lg',
                    'animate-in fade-in slide-in-from-bottom-2 duration-200',
                )}
            >
                {isLoading ? (
                    <span className="text-sm text-neutral-600 dark:text-neutral-400">Loading run...</span>
                ) : runDetail ? (
                    <>
                        <span className="text-sm text-neutral-700 dark:text-neutral-300" title={formatExactTime(runDetail.createdAt)}>
                            Viewing run from {formatRelativeTime(runDetail.createdAt)}
                        </span>

                        {/* Duration badge */}
                        <span
                            className={cn(
                                'text-xs px-2 py-0.5 rounded-full',
                                'bg-neutral-200/60 dark:bg-neutral-700/60',
                                'text-neutral-600 dark:text-neutral-400',
                            )}
                        >
                            {formatDuration(runDetail.duration)}
                        </span>

                        {/* Status badge */}
                        <span
                            className={cn(
                                'text-xs px-2 py-0.5 rounded-full',
                                runDetail.error
                                    ? 'bg-red-100/60 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                                    : 'bg-emerald-100/60 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
                            )}
                        >
                            {runDetail.error ? 'Error' : 'Success'}
                        </span>

                        {/* Compatibility warning */}
                        {compatibility && !compatibility.isFullyCompatible && (
                            <span className="text-xs text-amber-600 dark:text-amber-400 max-w-[260px] truncate">
                                {compatibility.message}
                            </span>
                        )}
                    </>
                ) : null}

                {/* Live preview toggle */}
                {onToggleLivePreview && (
                    <button
                        onClick={onToggleLivePreview}
                        aria-label={livePreviewEnabled ? 'Disable live preview' : 'Enable live preview'}
                        title={livePreviewEnabled ? 'Live preview on' : 'Live preview off'}
                        className={cn(
                            'p-1 rounded-md transition-colors ml-1',
                            livePreviewEnabled
                                ? 'text-amber-500 dark:text-amber-400 hover:bg-amber-100/50 dark:hover:bg-amber-900/30'
                                : 'text-neutral-400 dark:text-neutral-500 hover:bg-neutral-200/50 dark:hover:bg-neutral-700/50',
                        )}
                    >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                        </svg>
                    </button>
                )}

                {/* Close button */}
                <button
                    onClick={onClose}
                    aria-label="Close run view"
                    className={cn(
                        'p-1 rounded-md transition-colors ml-1',
                        'text-neutral-500 dark:text-neutral-400',
                        'hover:bg-neutral-200/50 dark:hover:bg-neutral-700/50',
                        'hover:text-neutral-700 dark:hover:text-neutral-200',
                    )}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>
    );
};
